import React from 'react';
import { isBrowser } from '@cp/utils/browser';
import { Box } from '@cp/ds/src/components/box';
import { TOAST_POSITIONS, TOAST_STEPS } from './const';
import { AnimationStyles } from './animtaionStyles';
import { ListsOfToasts } from './listsOfToasts';
import { IToastItem, IToastManager, ToastIdentifier, ToastPosition, ToastStepTypes } from './types';
import { useToastManager } from './useToastManager';
import { ToastManager } from './ToastManager';
import { SxProps } from '@cp/ds';

export type ToastStore = Record<ToastPosition, IToastItem[]>;
export type ToastActions =
  | {
      type: 'create';
      params: IToastItem;
    }
  | {
      type: 'delete';
      id: ToastIdentifier;
    }
  | {
      type: 'step';
      step: ToastStepTypes;
      id: ToastIdentifier;
    };

const toastReducer: React.Reducer<ToastStore, ToastActions> = (state, action) => {
  let store = { ...state };
  switch (action.type) {
    case 'create':
      store[action.params.position] = [...store[action.params.position], action.params];
      return store;
    case 'delete':
      const [deleteItem] = Object.values(store)
        .flat()
        .filter(({ id }) => id === action.id);
      if (!deleteItem) return store;
      store[deleteItem.position] = store[deleteItem.position].filter(({ id }) => id !== action.id);
      return store;
    case 'step':
      const [stepItem] = Object.values(store)
        .flat()
        .filter(({ id }) => id === action.id);
      if (!stepItem) return store;
      stepItem.step = action.step;
      store[stepItem.position] = store[stepItem.position].map((item) => (stepItem.id === item.id ? stepItem : item));
      return store;
    default:
      return store;
  }
};

const initialStore: ToastStore = {
  [TOAST_POSITIONS.BOTTOM_CENTER]: [],
  [TOAST_POSITIONS.BOTTOM_LEFT]: [],
  [TOAST_POSITIONS.BOTTOM_RIGHT]: [],
  [TOAST_POSITIONS.TOP_CENTER]: [],
  [TOAST_POSITIONS.TOP_LEFT]: [],
  [TOAST_POSITIONS.TOP_RIGHT]: [],
};

const createToastIdentifier = (): ToastIdentifier => 'toast/' + Date.now();

interface AppToastManagerProps {
  sx?: SxProps;
}

export const AppToastManager = (props: AppToastManagerProps) => {
  const manager = useToastManager();
  const managerRef = React.useRef<IToastManager>(null);
  const [store, dispatch] = React.useReducer(toastReducer, initialStore);

  if (isBrowser) {
    // https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useLayoutEffect(() => {
      if (!(manager instanceof ToastManager)) {
        console.warn('Can not connect toast ref to current manager');
        return;
      }
      manager.connect(managerRef);
      return () => manager.disconnect();
    }, [manager]);
  }

  React.useImperativeHandle(managerRef, () => ({
    create: (toast) => {
      if (toast.signal?.aborted) {
        console.warn('AbortController is aborted');
        return null;
      }
      const id = createToastIdentifier();
      dispatch({ type: 'create', params: { id, ...toast, step: TOAST_STEPS.INIT } });
      return id;
    },
    close: (toastId: ToastIdentifier) => {
      dispatch({ type: 'step', id: toastId, step: TOAST_STEPS.CLOSE });
    },
  }));

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999999,
        pointerEvents: 'none',
        ...props.sx,
      }}
    >
      <ListsOfToasts dispatch={dispatch} store={store} />
      <AnimationStyles />
    </Box>
  );
};
